import { useState, useEffect, useRef } from "react";
import {
  UploadCloud,
  Download,
  ChevronRight,
  File,
  Upload,
  X,
  Check,
  ChevronDown,
  ArrowLeft,
  AlertTriangle,
  Info,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getDownloadTemplate,
  uploadDataFile,
  getDataProgress,
  getUploadPreview,
  getSuggestions,
} from "../../api";
import { useFuzzyMatch } from "../../hooks";
import { ProgressBar } from "../ProgressBar";
import { logEvent } from "@shared/analytics/analytics";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { NavigationButtons } from "../shared/NavigationButtons";
import { PageHeader } from "@shared/components/PageHeader";

// File Selection Display Component
const FileSelectionDisplay = ({ file, onRemove, onUpload, isUploading }) => {
  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <File size={24} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">{file.name}</h3>
            <p className="text-sm text-gray-500">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={onUpload}
            disabled={isUploading}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              isUploading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            <Upload size={16} />
            <span>{isUploading ? "Uploading..." : "Upload"}</span>
          </button>
          <button
            onClick={onRemove}
            disabled={isUploading}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// File Upload Progress Component
const FileUploadProgress = ({ progress, fileName, status }) => {
  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 p-8">
      <div className="text-center">
        <div className="flex items-center justify-center mb-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <File size={32} className="text-blue-600" />
          </div>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Processing {fileName}
        </h3>
        <p className="text-sm text-gray-600 mb-4">{status}</p>
        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-sm text-gray-600">{progress}% complete</p>
      </div>
    </div>
  );
};

// ADD this component before the DataPreviewTable
const FileProcessingComplete = ({ stats }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center space-x-2 mb-4">
        <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
          <Check size={14} className="text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">
          File Processing Complete
        </h3>
      </div>

      <p className="text-gray-700 mb-6">
        Your file has been successfully processed and validated.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white rounded-lg p-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {stats?.rowsProcessed?.toLocaleString()}
          </div>
          <div className="text-sm text-gray-600">Rows Processed</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {stats?.columnsFound}
          </div>
          <div className="text-sm text-gray-600">Columns Found</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {stats?.fileSize}
          </div>
          <div className="text-sm text-gray-600">File Size</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {stats?.processingTime}
          </div>
          <div className="text-sm text-gray-600">Processing Time</div>
        </div>
      </div>
    </div>
  );
};

// Data Preview Table Component
const DataPreviewTable = ({
  data,
  totalRows,
  missingColumns,
}: {
  data: Record<string, any>[];
  totalRows: number | null;
  missingColumns?: string[];
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Data Available
          </h3>
          <p className="text-gray-600">
            No data was found in the uploaded file.
          </p>
        </div>
      </div>
    );
  }

  // Get all unique keys from data to create columns
  const getAllKeys = (dataArray: Record<string, any>[]): string[] => {
    const keySet = new Set<string>();
    dataArray.forEach((item) => {
      Object.keys(item).forEach((key) => keySet.add(key));
    });
    return Array.from(keySet);
  };

  const columns = getAllKeys(data);

  // Format column headers
  const formatColumnHeader = (key: string): string => {
    return key.replace(/_/g, " ").toUpperCase();
  };

  // Format cell values
  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    return String(value);
  };

  // Get cell styling based on field type
  const getCellStyling = (key: string, value: any): string => {
    const lowerKey = key.toLowerCase();

    if (lowerKey.includes("email")) return "text-blue-600";
    if (lowerKey.includes("id") || lowerKey.includes("no"))
      return "text-purple-600 font-medium";
    if (lowerKey.includes("date")) return "text-green-600";
    if (lowerKey.includes("balance") || lowerKey.includes("payment"))
      return "text-orange-600";
    if (!value || value === "-") return "text-gray-400 italic";

    return "text-gray-900";
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          File Preview {totalRows !== null ? `(${totalRows} rows)` : ""}
        </h3>{" "}
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          Upload Completed
        </span>
      </div>

      {/* Make outer wrapper scrollable on X-axis */}
      <div className="overflow-x-auto w-full">
        <table className="table-auto">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column: string) => (
                <th
                  key={column}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  {formatColumnHeader(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row: Record<string, any>, index: number) => (
              <tr key={index} className="hover:bg-gray-50">
                {columns.map((column: string) => (
                  <td
                    key={`${index}-${column}`}
                    className="px-6 py-4 whitespace-nowrap text-sm"
                  >
                    <span className={getCellStyling(column, row[column])}>
                      {formatCellValue(row[column])}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {missingColumns && missingColumns.length > 0 && (
        <div className="mx-6 my-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-yellow-800 mb-1">
                Template columns not found in uploaded file:
              </h4>
              <div className="flex flex-wrap gap-2 mt-2">
                {missingColumns.map((column: string) => (
                  <span
                    key={column}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-300"
                  >
                    {column}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-sm text-yellow-700">
                You can still proceed, but these columns will need to be mapped
                manually or left unmapped during the column mapping step.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
        <p className="text-sm text-gray-600">Showing {data.length} records</p>
      </div>
    </div>
  );
};
// Main File Upload Page Component
const FileUploadPage = () => {
  const navigate = useNavigate();
  const [migrationName, setMigrationName] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadState, setUploadState] = useState("idle"); // 'idle', 'selected', 'uploading', 'completed', 'error'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number | null>(null);
  const [fileSize, setFileSize] = useState<number>(null);
  const [totalColumns, setTotalColumns] = useState<number>(null);
  const [processingTime, setProcessingTime] = useState<number>(null);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [uploadEndTime, setUploadEndTime] = useState(null);
  const [sessionCode, setSessionCode] = useState<string>("");
  const [taskId, setTaskId] = useState<string>("");
  const [isContinueLoading, setIsContinueLoading] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();

  const fuzzyMatchMutation = useFuzzyMatch();

  const [error, setError] = useState(null);
  const hasLogged = useRef(false);
  const pollingInterval = useRef(null);
  const { state } = useLocation();

  useEffect(() => {
    if (!hasLogged.current && state) {
      hasLogged.current = true;
    }
  }, [state]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  const selectedType = state?.dataType;

  const selectedFields = state?.selected_fields;
  const templateId = state?.templateId;
  const uploadMethod = state?.uploadMethod;
  const mandatoryFields = state?.mandatoryFields || [];

  // const operationType = state?.operation_type || 1;
  const operation_type =
    typeof state?.operation_type === "number" ? state.operation_type : 1;

  const [apiData, setApiData] = useState(null);

  const startPolling = (sessionId: string) => {
    setIsPreviewLoading(true);

    pollingInterval.current = setInterval(async () => {
      try {
        const progressData = await getDataProgress(sessionId);

        setUploadProgress(progressData.percentage);
        setUploadStatus(progressData.status);
        setTotalRows(progressData.totalRows);

        // CHANGE: Check for completion with new status values
        if (
          progressData.percentage >= 100 &&
          progressData.status === "completed" // Changed from statusDisplay === "COMPLETED"
        ) {
          clearInterval(pollingInterval.current);
          setUploadEndTime(Date.now());

          // NEW: Fetch preview data separately
          try {
            const previewData = await getUploadPreview(sessionId);

            if (previewData.previewData && previewData.previewData.length > 0) {
              setApiData(previewData.previewData);
              setSessionCode(previewData.sessionCode);
              setTotalRows(previewData.metadata.totalRows);
              setTotalColumns(previewData.metadata.totalColumns);
              setFileSize(previewData.metadata.sizeMb);
              setProcessingTime(previewData.metadata.processingTime);
            }

            setTimeout(() => {
              setUploadState("completed");
              setIsPreviewLoading(false);
            }, 400);
          } catch (previewError) {
            toast({
              title: `${previewError.message}`,
              description: "",
            });
            setError("Failed to load preview data");
          }
        }
      } catch (error) {
        toast({
          title: `${error.message}`,
          description: "",
        });
        clearInterval(pollingInterval.current);
        setError("Failed to get upload progress");
        setUploadState("error");
      }
    }, 2000);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getProcessingTime = (processingTime: number) => {
    if (uploadStartTime && uploadEndTime) {
      const timeDiffMs = processingTime;
      const seconds = Math.round(timeDiffMs / 1000);

      if (seconds < 60) {
        return `${seconds}s`;
      } else {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
      }
    }
    return "N/A";
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!migrationName || migrationName.trim() === "") {
      setError("Please enter a migration name before uploading");
      return;
    }
    try {
      setUploadState("uploading");
      setError(null);
      setUploadProgress(0);
      setUploadStatus("Starting upload...");
      setUploadStartTime(Date.now());

      const uploadOptions = {
        dataTypeName: selectedType,
        operationType: state?.operation_type,
        dataType: state?.categoryKey,
        remoteUtilityId: remoteUtilityId,
      };

      // Add template_id only if template method was selected
      if (uploadMethod === "template" && templateId) {
        uploadOptions.templateId = templateId;
      }

      if (operation_type === 1 && selectedFields && selectedFields.length > 0) {
        uploadOptions.selectedFields = selectedFields;
      }

      const response = await uploadDataFile(
        selectedFile,
        migrationName,
        uploadOptions
      );

      // CHANGE: Store session_id and task_id
      if (response.sessionId) {
        setUploadId(response.sessionId);
        setTaskId(response.taskId);
        startPolling(response.sessionId);

        logEvent(`${selectedType} Data Migration Data Uploaded`, {
          fileName: migrationName,
          operation_type,
          sessionId: response.sessionId,
        });
      } else {
        throw new Error("No session ID received");
      }
    } catch (error) {
      toast({
        title: `${error.message}`,
        description: "",
      });
      setError(error.message || "Upload failed");
      setUploadState("error");
    }
  };

  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      const maxSizeInBytes = 20 * 1024 * 1024; // 20MB in bytes

      if (file.size > maxSizeInBytes) {
        setError(
          `File size exceeds 20MB limit. Your file is ${(
            file.size /
            1024 /
            1024
          ).toFixed(2)}MB`
        );
        setSelectedFile(null);
        setUploadState("idle");
        return;
      }

      setSelectedFile(file);
      setUploadState("selected");
      setError(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadState("idle");
    setError(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleContinue = async () => {
    setIsContinueLoading(true);
    try {
      // If using template, skip fuzzy match and navigate directly
      if (uploadMethod === "template" && templateId) {
        const fileColumns =
          apiData && apiData.length > 0 ? Object.keys(apiData[0]) : [];

        navigate("/onboarding/data-migration/data-mapping", {
          state: {
            migrationName,
            selectedType,
            uploadId,
            sessionCode,
            taskId,
            operation_type,
            dataTypeKey: getDataTypeKey(selectedType),
            fileColumns,
            fileName: selectedFile?.name,
            templateId, // ADD: Pass template ID
            uploadMethod, // ADD: Pass upload method
            isTemplateUpload: true, // ADD: Flag to indicate template upload
            step: 3,
          },
        });
        return; // Early return - skip fuzzy match logic
      }

      const fuzzyMatchResponse = await fuzzyMatchMutation.mutateAsync({
        sessionId: uploadId,
        dataType: state?.categoryKey,
        operationType: operation_type,
        selectedFields: operation_type === 1 ? selectedFields : undefined,
      });

      // Step 2: Poll until fuzzy match completes
      let fuzzyMatchComplete = false;
      let pollCount = 0;
      const maxPolls = 5;

      while (!fuzzyMatchComplete && pollCount < maxPolls) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const progressData = await getDataProgress(uploadId);
        if (
          progressData.percentage >= 100 &&
          progressData.status === "completed"
        ) {
          try {
            const suggestionsResponse = await getSuggestions(uploadId);

            const hasValidMappings = Object.values(
              suggestionsResponse.suggestions || {}
            ).some((field) => field.selectedSource);

            if (hasValidMappings) {
              fuzzyMatchComplete = true;
            }
          } catch (error) {
            toast({
              title: `${error.message}`,
              description: "",
            });
          }
        }

        pollCount++;
      }

      if (!fuzzyMatchComplete) {
        throw new Error("Fuzzy match timed out");
      }

      const fileColumns =
        apiData && apiData.length > 0 ? Object.keys(apiData[0]) : [];

      navigate("/onboarding/data-migration/data-mapping", {
        state: {
          migrationName,
          selectedType,
          uploadId,
          sessionCode,
          taskId,
          operation_type,
          dataTypeKey: getDataTypeKey(selectedType),
          fuzzyMatchComplete: true,
          fileColumns,
          fileName: selectedFile?.name,
          templateId: null, // ADD: null for independent uploads
          uploadMethod: "independent", // ADD: explicit method
          isTemplateUpload: false, // ADD: Flag for independent upload
          step: 3,
        },
      });
    } catch (error) {
      toast({
        title: `${error.message}`,
        description: "",
      });
      setError("Failed to analyze columns. Please try again.");
    } finally {
      setIsContinueLoading(false);
    }
  };
  const getDataTypeKey = (dataTypeName: string): number => {
    const dataTypeMap: Record<string, number> = {
      Meters: 1,
      "Meter Reading": 1,
      Consumers: 0,
      "Consumer Meter Mapping": 0,
      Payments: 0,
    };
    return dataTypeMap[dataTypeName] || 1;
  };

  const [isTemplateDownloaded, setIsTemplateDownloaded] = useState(false);

  // Download template handler
  const handleDownloadTemplate = async () => {
    try {
      const isUpdateMode = operation_type === 1;
      const additionalParams =
        isUpdateMode && selectedFields
          ? { is_update: 1, selected_fields: selectedFields }
          : undefined;

      // ✅ MODIFY: Pass additional params only when needed
      const data = await getDownloadTemplate(
        state.categoryKey,
        remoteUtilityId,
        additionalParams
      );

      // If `data` is a string (CSV text), convert it to Blob
      const blob =
        data instanceof Blob
          ? data
          : new Blob([data], { type: "text/csv;charset=utf-8;" });

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${state.dataType}-template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      setIsTemplateDownloaded(true);
    } catch (error) {
      toast({
        title: `${error.message}`,
        description: "",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-white">
      <ProgressBar
        currentStep={2}
        totalSteps={7}
        progress={28}
        currentStepName={"File Upload"}
      />

      {/* Main Content */}
      <main className="w-full mx-auto px-6 py-8">
        <div className="w-full mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <PageHeader
                title="AI-Powered Data Upload"
                subtitle="Upload your data and let AI help you integrate it seamlessly into your system."
              />
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-2">
              <div>
                <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2 mb-2">
                  <Info size={20} className="text-blue-500" /> Two ways to get
                  started:
                </h2>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>
                    • <strong>Download template:</strong> Click the "Download
                    Template" button to get a pre-formatted CSV file with the
                    required columns, then populate it with your data.
                  </li>
                  <li>
                    • <strong>Upload independent file:</strong> Upload your own
                    CSV file that includes the mandatory columns listed below.
                    Our AI will automatically map and process your data
                    accordingly.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {mandatoryFields && mandatoryFields.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start space-x-2">
              <div className="text-yellow-600 mt-1">⚠</div>
              <p className="text-sm text-yellow-800">
                This CSV must include: {mandatoryFields?.join(", ")}. Missing
                columns will block the upload.
              </p>
            </div>
          )}

          {/* Migration Name Input */}
          <div className="mb-8">
            {/* Migration Name Label */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700">
                Migration Name*
              </h3>
            </div>

            {/* Input and Download Button Row */}
            <div className="flex items-center justify-between gap-6">
              {/* LEFT SIDE - Migration Name Input */}
              <div className="flex-1">
                <input
                  type="text"
                  value={migrationName}
                  onChange={(e) => setMigrationName(e.target.value)}
                  placeholder="Enter migration name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              {/* RIGHT SIDE - Data Type Badge + Download Button */}
              <div className="flex items-center space-x-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {selectedType}
                </span>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  <Download size={16} />
                  <span>Download Template</span>
                  <ChevronDown size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* File Upload Area */}

          {/* File Upload Area */}
          {(error || (operation_type === 1 && !isTemplateDownloaded)) && (
            <p className="text-gray-500 text-center mb-4">
              Please download the template first to enable file upload.
            </p>
          )}

          {(operation_type !== 1 || isTemplateDownloaded) && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
              <div className="lg:col-span-1">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Quick Checklist
                  </h4>
                  <ul className="space-y-2 text-sm">
                    {[
                      "Name your migration",
                      "Save file as .csv",
                      "Verify mandatory columns",
                      "File ≤ 20 MB",
                      "Review data",
                      "Click map columns",
                    ].map((item, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                          <Check size={10} className="text-white" />
                        </div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="lg:col-span-3">
                {uploadState === "idle" && (
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 ${
                      isDragOver
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div
                        className={`p-4 rounded-full transition-colors duration-300 ${
                          isDragOver ? "bg-blue-100" : "bg-gray-100"
                        }`}
                      >
                        <UploadCloud
                          size={48}
                          className={`transition-colors duration-300 ${
                            isDragOver ? "text-blue-600" : "text-gray-400"
                          }`}
                        />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900 mb-2">
                          Drag & drop your file
                        </p>
                        <p className="text-sm text-gray-500 mb-4">
                          Supports .csv, .xlsx files up to 20MB
                        </p>
                        <label className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors duration-200">
                          <span>Browse Files</span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".csv,.xlsx,.xls"
                            onChange={(e) => handleFileSelect(e.target.files)}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {uploadState === "selected" && (
                  <FileSelectionDisplay
                    file={selectedFile}
                    onRemove={handleRemoveFile}
                    onUpload={handleUpload}
                    isUploading={false}
                  />
                )}

                {uploadState === "uploading" && (
                  <FileUploadProgress
                    progress={uploadProgress}
                    fileName={selectedFile?.name}
                    status={uploadStatus}
                  />
                )}

                {uploadState === "completed" && (
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 ${
                      isDragOver
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="flex flex-col items-center space-y-4">
                      <div
                        className={`p-4 rounded-full transition-colors duration-300 ${
                          isDragOver ? "bg-blue-100" : "bg-gray-100"
                        }`}
                      >
                        <UploadCloud
                          size={48}
                          className={`transition-colors duration-300 ${
                            isDragOver ? "text-blue-600" : "text-gray-400"
                          }`}
                        />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900 mb-2">
                          Drag & drop your file
                        </p>
                        <p className="text-sm text-gray-500 mb-4">
                          Supports .csv, .xlsx files up to 20MB
                        </p>
                        <label className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors duration-200">
                          <span>Browse Files</span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".csv,.xlsx,.xls"
                            onChange={(e) => handleFileSelect(e.target.files)}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {uploadState === "completed" && (
            <div className="w-full min-h-[200px]">
              {isPreviewLoading ? (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <svg
                    className="animate-spin h-5 w-5 text-blue-500"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <span>Preparing preview...</span>
                </div>
              ) : (
                <>
                  <FileProcessingComplete
                    stats={{
                      rowsProcessed: totalRows || apiData?.length,
                      columnsFound: totalColumns,
                      fileSize: `${fileSize} MB`,
                      processingTime: `${processingTime} sec`, // You can calculate this from upload start/end times
                    }}
                  />

                  <DataPreviewTable
                    data={apiData}
                    totalRows={totalRows}
                    missingColumns={apiData?.missingColumns} // need to test
                  />
                </>
              )}
            </div>
          )}

          {/* Continue Button */}
          {uploadState === "completed" && (
            <NavigationButtons
              onBack={handleBack}
              onContinue={handleContinue}
              continueText="Continue to Field Mapping"
              isLoading={isContinueLoading}
              canContinue={uploadState === "completed"}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default FileUploadPage;
